class ConnectorUpsellPopup extends HTMLElement {
  constructor() {
    super();

    this.tag = (this.dataset.tag || '').trim();
    this.variantId = this.dataset.variantId;
    this.pendingButton = null;
    this.pendingForm = null;

    this.addButton = this.querySelector('[data-connector-upsell-add]');
    this.errorEl = this.querySelector('#ConnectorUpsellPopupError');

    this.querySelectorAll('[data-connector-upsell-close]').forEach((el) => {
      el.addEventListener('click', this.close.bind(this));
    });

    const skipButton = this.querySelector('[data-connector-upsell-skip]');
    if (skipButton) skipButton.addEventListener('click', this.onSkip.bind(this));

    if (this.addButton) this.addButton.addEventListener('click', this.onAdd.bind(this));

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.hidden) this.close();
    });

    // Capture phase so we intercept the click before the form actually submits/navigates.
    document.addEventListener('click', this.onDocumentClick.bind(this), true);
  }

  onDocumentClick(event) {
    if (!this.tag) return;

    const checkoutButton = event.target.closest('button[name="checkout"], input[name="checkout"]');
    if (!checkoutButton) return;

    const form = checkoutButton.closest('form');
    if (!form) return;

    // Already resolved for this attempt (e.g. re-triggered via requestSubmit) — let it through.
    if (form.dataset.connectorUpsellResolved === 'true') {
      delete form.dataset.connectorUpsellResolved;
      return;
    }

    if (this.cartHasConnector(form)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    this.pendingButton = checkoutButton;
    this.pendingForm = form;
    this.resetState();
    this.open();
  }

  cartHasConnector(form) {
    const items = form.querySelectorAll('[data-product-tags]');
    for (const item of items) {
      const tags = (item.dataset.productTags || '')
        .split(',')
        .map((tag) => tag.trim().toLowerCase());
      if (tags.includes(this.tag.toLowerCase())) return true;
    }
    return false;
  }

  resetState() {
    if (this.errorEl) {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }
    this.classList.remove('loading');
    if (this.addButton) this.addButton.removeAttribute('disabled');
  }

  open() {
    this.hidden = false;
    document.body.classList.add('connector-upsell-popup--open');
  }

  close() {
    this.hidden = true;
    document.body.classList.remove('connector-upsell-popup--open');
    this.pendingButton = null;
    this.pendingForm = null;
  }

  continueToCheckout() {
    const form = this.pendingForm;
    const button = this.pendingButton;
    this.hidden = true;
    document.body.classList.remove('connector-upsell-popup--open');
    this.pendingButton = null;
    this.pendingForm = null;

    if (!form) return;

    form.dataset.connectorUpsellResolved = 'true';

    if (button && typeof form.requestSubmit === 'function') {
      form.requestSubmit(button);
    } else {
      form.submit();
    }
  }

  onSkip(event) {
    event.preventDefault();
    this.continueToCheckout();
  }

  onAdd(event) {
    event.preventDefault();
    if (!this.variantId) {
      this.continueToCheckout();
      return;
    }

    this.classList.add('loading');
    this.addButton.setAttribute('disabled', true);
    if (this.errorEl) this.errorEl.hidden = true;

    const body = JSON.stringify({ id: this.variantId, quantity: 1 });

    fetch(`${theme.routes.cart_add_url}`, { ...fetchConfig(), body })
      .then((response) => response.json())
      .then((data) => {
        if (data.status) {
          if (this.errorEl) {
            this.errorEl.hidden = false;
            this.errorEl.textContent = data.description || data.message || theme.cartStrings.error;
          }
          this.classList.remove('loading');
          this.addButton.removeAttribute('disabled');
          return;
        }

        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: data } }));
        this.continueToCheckout();
      })
      .catch(() => {
        if (this.errorEl) {
          this.errorEl.hidden = false;
          this.errorEl.textContent = theme.cartStrings.error;
        }
        this.classList.remove('loading');
        this.addButton.removeAttribute('disabled');
      });
  }
}

if (!customElements.get('connector-upsell-popup')) {
  customElements.define('connector-upsell-popup', ConnectorUpsellPopup);
}
